import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    X,
    Save,
    Loader2
} from 'lucide-react';

import { useSensorDictionaries } from './useSensorDictionaries';

import {
    API_URL,
    DEVICE_TYPE_NAMES,
    DEVICE_PLACEHOLDERS,
    HC_MIT_NUMBERS,
    formatDateForInput
} from './houseHelpers';

const EditDeviceModal = ({ type, device, onClose, onSave }) => {
    const {
        temperatureSensors,
        pressureSensors,
        flowMeters
    } = useSensorDictionaries();

    const [displayName, setDisplayName] = useState(device.displayName || '');
    const [factoryNumber, setFactoryNumber] = useState(device.factoryNumber || '');

    const [verificationDate, setVerificationDate] = useState(
        device.verificationDate
            ? device.verificationDate.split('.').reverse().join('-')
            : ''
    );

    const [validUntil, setValidUntil] = useState(
        device.verificationValidUntil
            ? device.verificationValidUntil.split('.').reverse().join('-')
            : ''
    );

    const [position, setPosition] = useState(device.position || '');

    const [sensorDeviceType, setSensorDeviceType] = useState(device.deviceType || '');
    const [sensorDeviceModel, setSensorDeviceModel] = useState(device.deviceModel || '');
    const [specs, setSpecs] = useState(device.specs || '');
    const [sensorReleaseDate, setSensorReleaseDate] = useState('');
    const [selectedSensorDict, setSelectedSensorDict] = useState(null);

    const [releaseDate, setReleaseDate] = useState(
        device.releaseDate
            ? device.releaseDate.split('.').reverse().join('-')
            : ''
    );

    const [crc16, setCrc16] = useState(device.crc16 || '');

    const [lastSettingsChange, setLastSettingsChange] = useState(
        device.lastSettingsChange
            ? device.lastSettingsChange.split('.').reverse().join('-')
            : ''
    );

    const [jumperPosition, setJumperPosition] = useState(
        device.jumperPosition || 'Изменения запрещены'
    );

    const [algorithmFormula, setAlgorithmFormula] = useState(device.algorithmFormula || '');
    const [coldSourceParam, setColdSourceParam] = useState(device.coldSourceParam || '');
    const [archiveDepth, setArchiveDepth] = useState(device.archiveDepth || '');

    const [loadingFgis, setLoadingFgis] = useState(false);

    const isHeatComputer = type === 'heat-computer';
    const isSensor = type !== 'heat-computer';

    const placeholders = DEVICE_PLACEHOLDERS[type] || {};

    const getSensorDictionaries = () => {
        if (type === 'temp-sensor') return temperatureSensors;
        if (type === 'pressure-sensor') return pressureSensors;
        if (type === 'flow-meter') return flowMeters;

        return [];
    };

    const fetchFgisData = async (serialNumber, mitNumber) => {
        if (!serialNumber || !mitNumber) return;

        setLoadingFgis(true);

        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(
                `${API_URL}/api/fgis-arshin/verify?serialNumber=${encodeURIComponent(serialNumber)}&mitNumber=${encodeURIComponent(mitNumber)}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.data && response.data.success !== false) {
                if (response.data.verificationDate) {
                    setVerificationDate(formatDateForInput(response.data.verificationDate));
                }

                if (response.data.validDate) {
                    setValidUntil(formatDateForInput(response.data.validDate));
                }

                if (response.data.manufactureYear) {
                    const year = response.data.manufactureYear;
                    const dateStr = `${year}-01-01`;

                    if (isHeatComputer) {
                        setReleaseDate(dateStr);
                    } else {
                        setSensorReleaseDate(dateStr);
                    }
                }
            }
        } catch (err) {
            console.error('Ошибка загрузки данных из ФГИС:', err);
        } finally {
            setLoadingFgis(false);
        }
    };

    useEffect(() => {
        if (isSensor && device.deviceType) {
            const dicts = getSensorDictionaries();

            const found = dicts.find(
                (d) => d.designation === device.deviceType || d.name === device.deviceType
            );

            if (found) {
                setSelectedSensorDict(found);
            }
        }
    }, [temperatureSensors, pressureSensors, flowMeters, device.deviceType]);

    useEffect(() => {
        if (isHeatComputer && factoryNumber && device.deviceType) {
            const mitNumber = HC_MIT_NUMBERS[device.deviceType];

            if (mitNumber) {
                fetchFgisData(factoryNumber, mitNumber);
            }
        }
    }, [factoryNumber, device.deviceType]);

    useEffect(() => {
        if (isSensor && factoryNumber && selectedSensorDict?.registryNumber) {
            fetchFgisData(factoryNumber, selectedSensorDict.registryNumber);
        }
    }, [factoryNumber, selectedSensorDict]);

    const handleSensorTypeChange = (e) => {
        const dictId = e.target.value;

        if (!dictId) {
            setSelectedSensorDict(null);
            setSensorDeviceType('');
            return;
        }

        const dicts = getSensorDictionaries();

        const selected = dicts.find((d) => d.id === parseInt(dictId, 10));

        if (selected) {
            setSelectedSensorDict(selected);
            setSensorDeviceType(selected.designation || selected.name);
        }
    };

    const handleSave = () => {
        if (isHeatComputer) {
            onSave({
                displayName: displayName || null,
                factoryNumber: factoryNumber || null,
                verificationDate: verificationDate || null,
                verificationValidUntil: validUntil || null,
                releaseDate: releaseDate || null,
                crc16: crc16 || null,
                lastSettingsChange: lastSettingsChange || null,
                jumperPosition: jumperPosition || null,
                algorithmFormula: algorithmFormula || null,
                coldSourceParam: coldSourceParam || null,
                archiveDepth: archiveDepth || null
            });
        } else {
            onSave({
                factoryNumber: factoryNumber || null,
                verificationDate: verificationDate || null,
                verificationValidUntil: validUntil || null,
                position: position || null,
                deviceType: sensorDeviceType || null,
                deviceModel: sensorDeviceModel || null,
                specs: specs || null,
                releaseDate: sensorReleaseDate || null,
                registryNumber: selectedSensorDict?.registryNumber || null
            });
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800 z-10">
                    <h3 className="text-white font-semibold">
                        {DEVICE_TYPE_NAMES[type]}: {device.position || device.displayName || device.deviceName || '—'}
                    </h3>

                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {isSensor ? (
                        <>
                            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                                <h4 className="text-white font-medium text-sm mb-3">
                                    Характеристики устройства
                                </h4>

                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-slate-400 text-xs mb-1">
                                                Тип СИ *
                                            </label>

                                            <select
                                                value={selectedSensorDict?.id || ''}
                                                onChange={handleSensorTypeChange}
                                                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                            >
                                                <option value="">— Выберите тип СИ —</option>

                                                {getSensorDictionaries().map((dict) => (
                                                    <option key={dict.id} value={dict.id}>
                                                        {dict.designation || dict.name} (рег. № {dict.registryNumber})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-slate-400 text-xs mb-1">
                                                Исполнение (Модель)
                                            </label>

                                            <input
                                                type="text"
                                                value={sensorDeviceModel}
                                                onChange={(e) => setSensorDeviceModel(e.target.value)}
                                                placeholder={placeholders.model || 'Модель устройства'}
                                                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-slate-400 text-xs mb-1">
                                            Переменная
                                        </label>

                                        <input
                                            type="text"
                                            value={position}
                                            onChange={(e) => setPosition(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-slate-400 text-xs mb-1">
                                            Технические характеристики
                                        </label>

                                        <textarea
                                            value={specs}
                                            onChange={(e) => setSpecs(e.target.value)}
                                            placeholder={placeholders.specs || 'Технические характеристики'}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm min-h-[100px] resize-y whitespace-pre-wrap"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                                <h4 className="text-white font-medium text-sm mb-3">
                                    Основная информация
                                </h4>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-slate-400 text-xs mb-1">
                                            Заводской номер
                                        </label>

                                        <input
                                            type="text"
                                            value={factoryNumber}
                                            onChange={(e) => setFactoryNumber(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-slate-400 text-xs mb-1">
                                            Дата поверки
                                        </label>

                                        <input
                                            type="date"
                                            value={verificationDate}
                                            onChange={(e) => setVerificationDate(e.target.value)}
                                            disabled={loadingFgis}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer disabled:opacity-60"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-slate-400 text-xs mb-1">
                                            Поверка действительна до
                                        </label>

                                        <input
                                            type="date"
                                            value={validUntil}
                                            onChange={(e) => setValidUntil(e.target.value)}
                                            disabled={loadingFgis}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer disabled:opacity-60"
                                        />
                                    </div>

                                    <div className="col-span-2">
                                        <label className="block text-slate-400 text-xs mb-1">
                                            Дата выпуска
                                        </label>

                                        <input
                                            type="date"
                                            value={sensorReleaseDate}
                                            onChange={(e) => setSensorReleaseDate(e.target.value)}
                                            disabled={loadingFgis}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer disabled:opacity-60"
                                        />
                                    </div>
                                </div>

                                {loadingFgis && (
                                    <div className="mt-3 flex items-center gap-2 text-blue-400 text-xs">
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Загрузка данных из ФГИС Аршин...
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <h4 className="text-white font-medium text-sm mb-3">
                                Основная информация
                            </h4>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Описание
                                    </label>

                                    <input
                                        type="text"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Заводской номер
                                    </label>

                                    <input
                                        type="text"
                                        value={factoryNumber}
                                        onChange={(e) => setFactoryNumber(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Дата поверки
                                    </label>

                                    <input
                                        type="date"
                                        value={verificationDate}
                                        onChange={(e) => setVerificationDate(e.target.value)}
                                        disabled={loadingFgis}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer disabled:opacity-60"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Поверка действительна до
                                    </label>

                                    <input
                                        type="date"
                                        value={validUntil}
                                        onChange={(e) => setValidUntil(e.target.value)}
                                        disabled={loadingFgis}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer disabled:opacity-60"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Дата выпуска
                                    </label>

                                    <input
                                        type="date"
                                        value={releaseDate}
                                        onChange={(e) => setReleaseDate(e.target.value)}
                                        disabled={loadingFgis}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer disabled:opacity-60"
                                    />
                                </div>
                            </div>

                            {loadingFgis && (
                                <div className="mt-3 flex items-center gap-2 text-blue-400 text-xs">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Загрузка данных из ФГИС Аршин...
                                </div>
                            )}
                        </div>
                    )}

                    {isHeatComputer && (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <h4 className="text-white font-medium text-sm mb-3">
                                Параметры программирования
                            </h4>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        CRC16 настроек
                                    </label>

                                    <input
                                        type="text"
                                        value={crc16}
                                        onChange={(e) => setCrc16(e.target.value)}
                                        placeholder="0x2DAC"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Дата посл. изм. настроек
                                    </label>

                                    <input
                                        type="date"
                                        value={lastSettingsChange}
                                        onChange={(e) => setLastSettingsChange(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Перемычка
                                    </label>

                                    <select
                                        value={jumperPosition}
                                        onChange={(e) => setJumperPosition(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    >
                                        <option value="Изменения запрещены">
                                            Изменения запрещены
                                        </option>

                                        <option value="Изменения разрешены">
                                            Изменения разрешены
                                        </option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Глубина архивов
                                    </label>

                                    <input
                                        type="text"
                                        value={archiveDepth}
                                        onChange={(e) => setArchiveDepth(e.target.value)}
                                        placeholder="4 года"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    />
                                </div>
                            </div>

                            <div className="space-y-3 mt-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Алгоритм учета
                                    </label>

                                    <textarea
                                        value={algorithmFormula}
                                        onChange={(e) => setAlgorithmFormula(e.target.value)}
                                        placeholder={`dt=t1-t2\ndP=P1-P2\nM1=p1V1\nM2=p2V2\ndGm=M1-M2\nQ1=M1 * (t1-t2)`}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm min-h-[100px] resize-y whitespace-pre-wrap"
                                    />
                                </div>

                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">
                                        Параметр холодного источника
                                    </label>

                                    <input
                                        type="text"
                                        value={coldSourceParam}
                                        onChange={(e) => setColdSourceParam(e.target.value)}
                                        placeholder="6,5 °C"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2 sticky bottom-0 bg-slate-800">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
                    >
                        Отмена
                    </button>

                    <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm flex items-center gap-2"
                    >
                        <Save className="w-4 h-4" />
                        Сохранить
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditDeviceModal;